import { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, MessageCircleMore, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import {
  buildPrescriptionShareMessage,
  getPrescriptionOwnerPhones,
  getPrescriptionSignedPublicUrl,
  getPrescriptionStatus,
  getPrescriptionValidationUrl,
  getSignedPrescriptionUrl,
  sharePrescription,
  type PrescriptionShareable,
} from "@/lib/prescriptions/sharing";

type PrescriptionWithShareState = PrescriptionShareable;

type LoosePrescriptionClient = {
  select: (columns: string) => {
    eq: (column: string, value: string) => {
      single: () => Promise<{ data: unknown; error: Error | null }>;
    };
  };
};

const getLoosePrescriptionClient = () =>
  (supabase as unknown as { from: (name: string) => LoosePrescriptionClient }).from("medical_prescriptions");

interface PrescriptionShareDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prescription: PrescriptionWithShareState;
  onRequireSign?: () => Promise<void>;
  isSigning?: boolean;
}

export function PrescriptionShareDrawer({
  open,
  onOpenChange,
  prescription,
  onRequireSign,
  isSigning = false,
}: PrescriptionShareDrawerProps) {
  const { t } = useTranslation("prescriptions");
  const [livePrescription, setLivePrescription] = useState(prescription);
  const [selectedPhone, setSelectedPhone] = useState("");
  const [message, setMessage] = useState("");
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);
  const [isPreparingShare, setIsPreparingShare] = useState(false);
  const initializedDocument = useRef<string | null>(null);
  const messageEdited = useRef(false);

  const ownerPhones = useMemo(() => getPrescriptionOwnerPhones(livePrescription), [livePrescription]);
  const status = getPrescriptionStatus(livePrescription);
  const validationUrl = getPrescriptionValidationUrl(livePrescription.id);
  const signedPdfPublicUrl = useMemo(
    () => getPrescriptionSignedPublicUrl(livePrescription),
    [livePrescription],
  );

  useEffect(() => {
    setLivePrescription(prescription);
  }, [prescription]);

  useEffect(() => {
    if (!open) {
      initializedDocument.current = null;
      return;
    }
    if (initializedDocument.current === livePrescription.id) return;
    initializedDocument.current = livePrescription.id;
    messageEdited.current = false;
    setSignedPdfUrl(null);
    const defaultPhone = ownerPhones[0]?.phone_number ?? "";
    setSelectedPhone(defaultPhone);
    setMessage(buildPrescriptionShareMessage(livePrescription, signedPdfPublicUrl));
  }, [livePrescription, open, ownerPhones, signedPdfPublicUrl, signedPdfUrl]);

  useEffect(() => {
    if (!open || !livePrescription.signed_at || !livePrescription.signed_storage_path) return;

    let isMounted = true;
    void (async () => {
      try {
        const nextUrl = await getSignedPrescriptionUrl(livePrescription);
        if (isMounted) {
          setSignedPdfUrl(nextUrl);
          if (!messageEdited.current) setMessage(buildPrescriptionShareMessage(livePrescription, nextUrl));
        }
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [livePrescription, open]);

  const refreshSignedPrescriptionState = async () => {
    const { data, error } = await getLoosePrescriptionClient()
      .select("signed_at, access_code, access_code_hint, signed_storage_bucket, signed_storage_path")
      .eq("id", livePrescription.id)
      .single();

    if (error) throw error;
    if (!data || typeof data !== "object") return livePrescription;

    const nextPrescription = {
      ...livePrescription,
      ...(data as Partial<PrescriptionWithShareState>),
    };
    setLivePrescription(nextPrescription);
    return nextPrescription;
  };

  const handleSend = async () => {
    let currentPrescription = livePrescription;

    if (!currentPrescription.signed_at) {
      if (!onRequireSign) {
        toast.error(t("share.mustSignFirst"));
        return;
      }

      await onRequireSign();
      currentPrescription = await refreshSignedPrescriptionState();
    }

    if (!selectedPhone) {
      toast.error(t("share.phoneRequired"));
      return;
    }

    setIsPreparingShare(true);
    try {
      const shareUrl = signedPdfUrl ?? await getSignedPrescriptionUrl(currentPrescription);
      if (!shareUrl) throw new Error("signed_url_missing");

      const nextMessage = message.trim() || buildPrescriptionShareMessage(currentPrescription, shareUrl);
      await sharePrescription({
        prescription: currentPrescription,
        phoneNumber: selectedPhone,
        signedPdfUrl: shareUrl,
        message: nextMessage,
      });

      if (!signedPdfUrl) setSignedPdfUrl(shareUrl);
      toast.success(t("share.sentSuccess"));
    } catch (error) {
      console.error(error);
      toast.error(t("share.sendError"));
    } finally {
      setIsPreparingShare(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-hidden p-0 flex flex-col">
        <DialogHeader className="space-y-3 border-b border-border/70 px-6 py-5">
          <DialogTitle>{t("share.title")}</DialogTitle>
          <DialogDescription>
            {status === "draft_issued" ? t("share.descriptionUnsigned") : t("share.descriptionSigned")}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-5">
          <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
            <div className="grid gap-3">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t("share.summaryRecipient")}
                </p>
                <p className="text-sm font-medium">
                  {livePrescription.pets?.owners?.name || t("share.missingOwner")}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prescription-share-phone">{t("share.phoneLabel")}</Label>
              <Input
                id="prescription-share-phone"
                type="tel"
                list={ownerPhones.length > 0 ? "prescription-share-phone-suggestions" : undefined}
                value={selectedPhone}
                placeholder={t("share.phonePlaceholder")}
                onChange={(event) => setSelectedPhone(event.target.value)}
              />
              {ownerPhones.length > 0 ? (
                <datalist id="prescription-share-phone-suggestions">
                  {ownerPhones.map((phone, index) => {
                    const value = phone.phone_number ?? "";
                    return <option key={phone.id ?? `${value}-${index}`} value={value} />;
                  })}
                </datalist>
              ) : (
                <div className="rounded-lg border border-dashed border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                  {t("share.noWhatsappPhone")}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="prescription-share-message">{t("share.messageLabel")}</Label>
              <Textarea
                id="prescription-share-message"
                rows={5}
                value={message}
                onChange={(event) => {
                  messageEdited.current = true;
                  setMessage(event.target.value);
                }}
              />
            </div>

            <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-sm font-medium">{t("share.validationTitle")}</p>
                  <p className="text-xs text-muted-foreground">{t("share.validationDescription")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" asChild>
                    <a href={validationUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t("share.openValidation")}
                    </a>
                  </Button>
                  {(signedPdfUrl ?? signedPdfPublicUrl) ? (
                    <Button type="button" variant="outline" size="sm" asChild>
                      <a href={signedPdfUrl ?? signedPdfPublicUrl ?? "#"} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t("share.openSignedPdf")}
                      </a>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border/70 px-6 py-4">
          {!livePrescription.signed_at ? (
            <Button type="button" onClick={handleSend} disabled={!onRequireSign || isSigning}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              {isSigning ? t("share.signingAndPreparing") : t("share.signAndSend")}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSend}
              disabled={isPreparingShare}
            >
              <MessageCircleMore className="mr-2 h-4 w-4" />
              {isPreparingShare ? t("share.preparing") : t("share.sendNow")}
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t("share.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
